<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div>
    <div class="field">
      <FieldLabel
        :label="$t('dashboardAdmin.blockEditor.favoritesFilter')"
        :help="$t('dashboardAdmin.blockEditor.help.favoritesFilter')"
      />
      <PrimeSelect
        :model-value="config.filter"
        :options="filterOptions"
        option-label="label"
        option-value="value"
        class="select-full"
        @update:model-value="updateFilter"
      />
    </div>
    <div v-if="config.filter !== 'site'" class="field">
      <FieldLabel
        :label="$t('dashboardAdmin.blockEditor.favoritesSiteId')"
        :help="$t('dashboardAdmin.blockEditor.help.favoritesSiteId')"
      />
      <PrimeAutoComplete
        :model-value="selectedSite"
        :suggestions="filteredSites"
        option-label="title"
        :placeholder="
          $t('dashboardAdmin.blockEditor.favoritesSiteIdPlaceholder')
        "
        class="autocomplete-full"
        dropdown
        forceSelection
        @complete="searchSites"
        @item-select="onSiteSelect"
        @clear="onSiteClear"
      />
    </div>
  </div>
</template>

<script>
import Select from "primevue/select";
import AutoComplete from "primevue/autocomplete";
import FieldLabel from "@/components/dashboard/editor/FieldLabel.vue";
import workspaceService from "@/services/WorkspaceService.js";

export default {
  name: "FavoritesConfig",
  components: {
    PrimeSelect: Select,
    PrimeAutoComplete: AutoComplete,
    FieldLabel,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:config"],
  data() {
    return {
      sites: [],
      filteredSites: [],
      selectedSite: null,
    };
  },
  computed: {
    filterOptions() {
      return [
        {
          label: this.$t("dashboardAdmin.blockEditor.favoritesFilterFile"),
          value: "file",
        },
        {
          label: this.$t("dashboardAdmin.blockEditor.favoritesFilterFolder"),
          value: "folder",
        },
        {
          label: this.$t("dashboardAdmin.blockEditor.favoritesFilterSite"),
          value: "site",
        },
      ];
    },
  },
  mounted() {
    this.loadSites();
  },
  methods: {
    loadSites() {
      workspaceService
        .getWorkspaces()
        .then((sites) => {
          this.sites = sites;
          // Restore selected site from config
          if (this.config.siteId) {
            this.selectedSite =
              sites.find((s) => s.id === this.config.siteId) || null;
          }
        })
        .catch((err) => {
          console.error("Error loading workspaces:", err);
        });
    },
    searchSites(event) {
      const query = event.query.toLowerCase();
      this.filteredSites = this.sites.filter(
        (site) =>
          site.title.toLowerCase().includes(query) ||
          site.id.toLowerCase().includes(query),
      );
    },
    onSiteSelect(event) {
      this.selectedSite = event.value;
      this.$emit("update:config", {
        ...this.config,
        siteId: event.value.id,
      });
    },
    onSiteClear() {
      this.selectedSite = null;
      const updated = { ...this.config };
      delete updated.siteId;
      this.$emit("update:config", updated);
    },
    updateFilter(value) {
      const updated = { ...this.config, filter: value };
      if (value === "site") {
        delete updated.siteId;
        this.selectedSite = null;
      }
      this.$emit("update:config", updated);
    },
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 1rem;
}

.select-full {
  width: 100%;
}

.autocomplete-full {
  width: 100%;
}

:deep(.p-autocomplete) {
  width: 100%;
}
</style>
